'use client';

import { useMemo, useCallback } from 'react';
import {
  useGetMyBookmarksApi,
  usePostBookmarkToggleApi,
  BookmarkToggleRequest,
} from '@/shared/apis/bookmark';
import { DtoFolderItemType } from '@/shared/constants';

/**
 * 북마크 키 생성 함수
 * @param targetType - 대상 타입 (0: FOLDER, 1: CONTENT, 2: PROJECT)
 * @param targetId - 대상 ID
 * @returns 북마크 키 문자열
 */
const createBookmarkKey = (
  targetType: DtoFolderItemType,
  targetId: number,
): string => `${targetType}-${targetId}`;

/**
 * 북마크 Set을 반환하는 훅
 * O(1) 시간복잡도로 북마크 여부를 확인할 수 있습니다.
 *
 * @returns 북마크 키 Set
 */
export const useBookmarkSet = () => {
  const { data: bookmarksResponse } = useGetMyBookmarksApi();

  return useMemo(() => {
    const set = new Set<string>();
    bookmarksResponse?.data?.forEach((bookmark) => {
      set.add(createBookmarkKey(bookmark.targetType, bookmark.targetId));
    });
    return set;
  }, [bookmarksResponse]);
};

/**
 * 특정 아이템의 북마크 여부를 확인하는 훅
 *
 * @param targetType - 대상 타입 (0: FOLDER, 1: CONTENT, 2: PROJECT)
 * @param targetId - 대상 ID
 * @returns 북마크 여부
 */
export const useIsBookmarked = (
  targetType: DtoFolderItemType,
  targetId: number,
): boolean => {
  const bookmarkSet = useBookmarkSet();
  return bookmarkSet.has(createBookmarkKey(targetType, targetId));
};

/**
 * 북마크 토글 기능을 제공하는 훅
 *
 * @returns { toggleBookmark, isLoading }
 */
export const useToggleBookmark = () => {
  const { mutate, isPending } = usePostBookmarkToggleApi();

  const toggleBookmark = useCallback(
    (request: BookmarkToggleRequest) => {
      mutate(request);
    },
    [mutate],
  );

  return {
    toggleBookmark,
    isLoading: isPending,
  };
};

/**
 * 북마크 관련 모든 기능을 제공하는 통합 훅
 *
 * @returns { bookmarkSet, isBookmarked, toggleBookmark, isLoading }
 */
export const useBookmark = () => {
  const bookmarkSet = useBookmarkSet();
  const { toggleBookmark, isLoading } = useToggleBookmark();

  const isBookmarked = useCallback(
    (targetType: DtoFolderItemType, targetId: number): boolean => {
      return bookmarkSet.has(createBookmarkKey(targetType, targetId));
    },
    [bookmarkSet],
  );

  return {
    bookmarkSet,
    isBookmarked,
    toggleBookmark,
    isLoading,
  };
};
